import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Wallet, DollarSign, CheckCircle, Clock, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { cn, formatCurrency, getStatusColor } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const Payroll = () => {
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [formData, setFormData] = useState({
    teacher_id: '',
    month: '',
    year: new Date().getFullYear(),
    basic_salary: '',
    house_allowance: 0,
    transport_allowance: 0,
    medical_allowance: 0,
    other_allowances: 0,
    loan_deduction: 0,
    other_deductions: 0
  });
  const [generateData, setGenerateData] = useState({
    month: months[new Date().getMonth()],
    year: new Date().getFullYear()
  });

  useEffect(() => {
    fetchPayroll();
    fetchTeachers();
  }, []);

  const fetchPayroll = async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/payroll`);
      setPayrollRecords(response.data);
    } catch (error) {
      console.error('Failed to fetch payroll:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API_URL}/teachers`);
      setTeachers(response.data);
    } catch (error) {
      console.error('Failed to fetch teachers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/finance/payroll`, {
        ...formData,
        basic_salary: parseFloat(formData.basic_salary),
        house_allowance: parseFloat(formData.house_allowance) || 0,
        transport_allowance: parseFloat(formData.transport_allowance) || 0,
        medical_allowance: parseFloat(formData.medical_allowance) || 0,
        other_allowances: parseFloat(formData.other_allowances) || 0,
        loan_deduction: parseFloat(formData.loan_deduction) || 0,
        other_deductions: parseFloat(formData.other_deductions) || 0
      });
      fetchPayroll();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create payroll:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      teacher_id: '',
      month: '',
      year: new Date().getFullYear(),
      basic_salary: '',
      house_allowance: 0,
      transport_allowance: 0,
      medical_allowance: 0,
      other_allowances: 0,
      loan_deduction: 0,
      other_deductions: 0
    });
  };

  const handleGenerate = async () => {
    try {
      await axios.post(`${API_URL}/finance/payroll/generate?month=${generateData.month}&year=${generateData.year}`);
      fetchPayroll();
      setGenerateDialogOpen(false);
    } catch (error) {
      console.error('Failed to generate payroll:', error);
    }
  };

  const handlePay = async (id) => {
    try {
      await axios.put(`${API_URL}/finance/payroll/${id}/pay`);
      fetchPayroll();
    } catch (error) {
      console.error('Failed to process payment:', error);
    }
  };

  const downloadPayslip = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/finance/payroll/${id}/payslip`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download payslip:', error);
    }
  };

  const exportExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/export/payroll/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_export.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export Excel:', error);
    }
  };

  const viewDetails = (payroll) => {
    setSelectedPayroll(payroll);
    setDetailDialogOpen(true);
  };

  const totalPending = payrollRecords.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.net_salary, 0);
  const totalPaid = payrollRecords.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.net_salary, 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded animate-pulse w-48" />
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-slate-200 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payroll Management</h1>
          <p className="text-slate-600 mt-1">Kenya payroll with PAYE, NHIF, NSSF & Housing Levy</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button 
            variant="outline"
            onClick={exportExcel}
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button 
            variant="outline"
            onClick={() => setGenerateDialogOpen(true)}
            data-testid="generate-payroll-btn"
          >
            Generate Payroll
          </Button>
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="add-payroll-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Record
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Records</p>
              <h3 className="text-2xl font-bold text-slate-900">{payrollRecords.length}</h3>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Pending Payment</p>
              <h3 className="text-2xl font-bold text-amber-600 font-mono">{formatCurrency(totalPending)}</h3>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Paid</p>
              <h3 className="text-2xl font-bold text-emerald-600 font-mono">{formatCurrency(totalPaid)}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Payroll Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Period</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Gross</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">PAYE</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">NHIF</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">NSSF</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Housing</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Net Pay</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payrollRecords.length > 0 ? (
                payrollRecords.map((record) => (
                  <tr key={record.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{record.teacher_name}</p>
                        <p className="text-xs text-slate-500">{record.employee_id}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-700">{record.month} {record.year}</td>
                    <td className="px-4 py-4 text-sm text-right font-mono text-slate-700">{formatCurrency(record.gross_salary)}</td>
                    <td className="px-4 py-4 text-sm text-right font-mono text-red-600">{formatCurrency(record.paye)}</td>
                    <td className="px-4 py-4 text-sm text-right font-mono text-red-600">{formatCurrency(record.nhif)}</td>
                    <td className="px-4 py-4 text-sm text-right font-mono text-red-600">{formatCurrency(record.nssf)}</td>
                    <td className="px-4 py-4 text-sm text-right font-mono text-red-600">{formatCurrency(record.housing_levy)}</td>
                    <td className="px-4 py-4 text-sm text-right font-mono font-bold text-emerald-600">{formatCurrency(record.net_salary)}</td>
                    <td className="px-4 py-4 text-center">
                      <span className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset capitalize',
                        getStatusColor(record.status)
                      )}>
                        {record.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => viewDetails(record)}
                          data-testid={`view-details-${record.id}`}
                        >
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => downloadPayslip(record.id)}
                          data-testid={`download-payslip-${record.id}`}
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        {record.status === 'pending' && (
                          <Button 
                            size="sm"
                            onClick={() => handlePay(record.id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`pay-btn-${record.id}`}
                          >
                            Pay
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center">
                    <div className="text-slate-500">
                      <Wallet className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="font-medium">No payroll records found</p>
                      <p className="text-sm mt-1">Generate payroll for all teachers or add individual records</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Record Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Payroll Record</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="teacher_id">Teacher</Label>
                <Select 
                  value={formData.teacher_id} 
                  onValueChange={(value) => {
                    const teacher = teachers.find(t => t.id === value);
                    setFormData({ 
                      ...formData, 
                      teacher_id: value,
                      basic_salary: teacher?.salary?.toString() || ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="payroll-teacher-select">
                    <SelectValue placeholder="Select teacher" />
                  </SelectTrigger>
                  <SelectContent>
                    {teachers.map((teacher) => (
                      <SelectItem key={teacher.id} value={teacher.id}>
                        {teacher.name} ({teacher.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="month">Month</Label>
                  <Select 
                    value={formData.month} 
                    onValueChange={(value) => setFormData({ ...formData, month: value })}
                  >
                    <SelectTrigger data-testid="payroll-month-select">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month) => (
                        <SelectItem key={month} value={month}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="year">Year</Label>
                  <Input
                    id="year"
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    required
                    data-testid="payroll-year-input"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-3">Earnings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="basic_salary">Basic Salary (KES)</Label>
                    <Input
                      id="basic_salary"
                      type="number"
                      value={formData.basic_salary}
                      onChange={(e) => setFormData({ ...formData, basic_salary: e.target.value })}
                      required
                      data-testid="basic-salary-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="house_allowance">House Allowance</Label>
                    <Input
                      id="house_allowance"
                      type="number"
                      value={formData.house_allowance}
                      onChange={(e) => setFormData({ ...formData, house_allowance: e.target.value })}
                      data-testid="house-allowance-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="transport_allowance">Transport Allowance</Label>
                    <Input
                      id="transport_allowance"
                      type="number"
                      value={formData.transport_allowance}
                      onChange={(e) => setFormData({ ...formData, transport_allowance: e.target.value })}
                      data-testid="transport-allowance-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="medical_allowance">Medical Allowance</Label>
                    <Input
                      id="medical_allowance"
                      type="number"
                      value={formData.medical_allowance}
                      onChange={(e) => setFormData({ ...formData, medical_allowance: e.target.value })}
                      data-testid="medical-allowance-input"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-3">Other Deductions</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="loan_deduction">Loan Deduction</Label>
                    <Input
                      id="loan_deduction"
                      type="number"
                      value={formData.loan_deduction}
                      onChange={(e) => setFormData({ ...formData, loan_deduction: e.target.value })}
                      data-testid="loan-deduction-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="other_deductions">Other Deductions</Label>
                    <Input
                      id="other_deductions"
                      type="number"
                      value={formData.other_deductions}
                      onChange={(e) => setFormData({ ...formData, other_deductions: e.target.value })}
                      data-testid="other-deductions-input"
                    />
                  </div>
                </div>
              </div>

              <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                <strong>Note:</strong> PAYE, NHIF, NSSF, and Housing Levy will be automatically calculated based on Kenya 2024 tax rates.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-payroll-btn">
                Add Record
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Generate Payroll Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Payroll</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              This will create payroll records with Kenya statutory deductions for all active teachers.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gen_month">Month</Label>
                <Select 
                  value={generateData.month} 
                  onValueChange={(value) => setGenerateData({ ...generateData, month: value })}
                >
                  <SelectTrigger data-testid="generate-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gen_year">Year</Label>
                <Input
                  id="gen_year"
                  type="number"
                  value={generateData.year}
                  onChange={(e) => setGenerateData({ ...generateData, year: parseInt(e.target.value) })}
                  data-testid="generate-year-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-generate-btn">
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Payroll Details</DialogTitle>
          </DialogHeader>
          {selectedPayroll && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-900">{selectedPayroll.teacher_name}</h4>
                <p className="text-sm text-slate-500">{selectedPayroll.employee_id} • {selectedPayroll.month} {selectedPayroll.year}</p>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-emerald-50 px-4 py-2 border-b">
                  <h5 className="font-semibold text-emerald-800">Earnings</h5>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Basic Salary</span>
                    <span className="font-mono">{formatCurrency(selectedPayroll.basic_salary)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">House Allowance</span>
                    <span className="font-mono">{formatCurrency(selectedPayroll.house_allowance)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Transport Allowance</span>
                    <span className="font-mono">{formatCurrency(selectedPayroll.transport_allowance)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Medical Allowance</span>
                    <span className="font-mono">{formatCurrency(selectedPayroll.medical_allowance)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                    <span>Gross Salary</span>
                    <span className="font-mono text-emerald-600">{formatCurrency(selectedPayroll.gross_salary)}</span>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-red-50 px-4 py-2 border-b">
                  <h5 className="font-semibold text-red-800">Statutory Deductions (Kenya)</h5>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">PAYE (Income Tax)</span>
                    <span className="font-mono text-red-600">{formatCurrency(selectedPayroll.paye)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">NHIF</span>
                    <span className="font-mono text-red-600">{formatCurrency(selectedPayroll.nhif)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">NSSF (Tier I & II)</span>
                    <span className="font-mono text-red-600">{formatCurrency(selectedPayroll.nssf)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Housing Levy (1.5%)</span>
                    <span className="font-mono text-red-600">{formatCurrency(selectedPayroll.housing_levy)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Loan Deduction</span>
                    <span className="font-mono text-red-600">{formatCurrency(selectedPayroll.loan_deduction)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                    <span>Total Deductions</span>
                    <span className="font-mono text-red-600">{formatCurrency(selectedPayroll.total_deductions)}</span>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-blue-50 px-4 py-2 border-b">
                  <h5 className="font-semibold text-blue-800">Tax Reliefs Applied</h5>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Personal Relief</span>
                    <span className="font-mono text-blue-600">{formatCurrency(selectedPayroll.personal_relief)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Insurance Relief</span>
                    <span className="font-mono text-blue-600">{formatCurrency(selectedPayroll.insurance_relief)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-indigo-900 text-white p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Net Pay</span>
                  <span className="text-2xl font-bold font-mono">{formatCurrency(selectedPayroll.net_salary)}</span>
                </div>
              </div>

              <Button 
                className="w-full bg-indigo-600 hover:bg-indigo-700" 
                onClick={() => downloadPayslip(selectedPayroll.id)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Payslip (PDF)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payroll;
